import { useCallback, useEffect } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { FaSearch } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
    setIdType,
    selectIdType,
    selectIdError,
    selectIdPersonType,
    selectIdIsForeigner,
} from '../../slices/idSlice';
import { useGetContificoPersonDataMutation } from '../../slices/contificoPersonApiSlice';
import { toast } from 'react-toastify';
import { EC_TYPE_CED, EC_TYPE_RUC } from '../../constants';
import Loader from '../layout/Loader';

const DocIdGroup = ({
    docId,
    setDocId,
    taxDocId,
    setTaxDocId,
    type,
    setType,
    isForeigner,
    setIsForeigner,
    fillForm,
    setValidDocId,
}) => {
    const dispatch = useDispatch();

    const idTypeSelect = useSelector(selectIdType);
    const idErrorSelect = useSelector(selectIdError);
    const idPersonTypeSelect = useSelector(selectIdPersonType);
    const idIsForeignerSelect = useSelector(selectIdIsForeigner);

    const [getContificoCtfPerson, { isLoading: isLoadingCtfPerson }] =
        useGetContificoPersonDataMutation();

    const validateDocId = useCallback(() => {
        dispatch(setIdType({ id: docId, isForeigner, personType: type }));
        if (type) {
            setValidDocId(true);
        }
    }, [dispatch, docId, isForeigner, setValidDocId, type]);

    const handleChange = (e) => {
        const selectedType = e.target.value;
        setType(selectedType);
        setIsForeigner(selectedType === 'NF');
    };

    const handleSearchCtf = async () => {
        validateDocId();
        if (docId.trim().length > 9 && !docId.includes('9999999999') && idTypeSelect) {
            try {
                let cPerson = await getContificoCtfPerson({ docId }).unwrap();
                if (Array.isArray(cPerson) && cPerson[0]) {
                    fillForm(cPerson);
                    toast.success('Sus datos han sido validados con Comercial Santy');
                    return;
                }

                // Validaciones cruzadas RUC <-> Cédula
                switch (true) {
                    case idTypeSelect === EC_TYPE_RUC && type === 'N':
                        cPerson = await getContificoCtfPerson({
                            docId: docId.slice(0, -3),
                        }).unwrap();
                        if (Array.isArray(cPerson) && cPerson[0]) {
                            fillForm(cPerson);
                            toast.success('Su cédula ha sido validada con Comercial Santy');
                        } else {
                            toast.warn(
                                'Usted no está registrado en Comercial Santy, por favor ingrese sus datos'
                            );
                        }
                        break;
                    case idTypeSelect === EC_TYPE_CED && type === 'N':
                        cPerson = await getContificoCtfPerson({ docId: docId + '001' }).unwrap();
                        if (Array.isArray(cPerson) && cPerson[0]) {
                            fillForm(cPerson);
                            toast.success('Su RUC ha sido validado con Comercial Santy');
                        } else {
                            toast.warn(
                                'Usted no está registrado en Comercial Santy, por favor ingrese sus datos'
                            );
                        }
                        break;
                    default:
                        toast.warn(
                            'Usted no está registrado en Comercial Santy, por favor ingrese sus datos'
                        );
                        break;
                }
            } catch (err) {
                toast.error('Error: ' + (err?.data?.message || err.error));
            }
        } else {
            toast.error('Por favor ingrese su Cédula o RUC correctamente antes de buscar');
        }
    };

    useEffect(() => {
        if (docId.length === 10 || docId.length === 13 || (docId.length > 6 && isForeigner)) {
            validateDocId();
        } else {
            dispatch(setIdType({ id: '', isForeigner: false, personType: '' }));
        }
    }, [dispatch, docId, isForeigner, type, validateDocId]);

    useEffect(() => {
        setType(idPersonTypeSelect);
        setIsForeigner(idIsForeignerSelect);
        if (!taxDocId) {
            if (idTypeSelect === EC_TYPE_RUC) {
                setTaxDocId(docId);
            } else {
                setTaxDocId('');
            }
        }
    }, [
        idTypeSelect,
        idErrorSelect,
        idPersonTypeSelect,
        idIsForeignerSelect,
        docId,
        taxDocId,
        setType,
        setIsForeigner,
        setTaxDocId,
    ]);

    return (
        <>
            <Form.Group controlId="docId" className="mb-3">
                <Form.Label className="fw-bold" column='lg'>
                    {type === 'NF' ? 'Identificación' : 'Cédula/RUC'}
                </Form.Label>
                <InputGroup className="mb-3">
                    <InputGroup.Text className="bg-light fw-bold">
                        {idTypeSelect && idTypeSelect !== 'unknown' ? idTypeSelect.toUpperCase() : 'ID'}
                    </InputGroup.Text>
                    <Form.Control
                        isInvalid={idErrorSelect}
                        required={!taxDocId}
                        type="text"
                        inputMode={type === 'NF' ? 'text' : 'numeric'}
                        placeholder="Ingrese su Cédula o RUC"
                        value={docId}
                        onChange={(e) => {
                            const input = e.target.value;
                            const numericInput = input.replace(/\D/g, '');
                            setDocId(numericInput);
                        }}
                        // Removed onBlur handler to prevent automatic search on focus loss
                        minLength={!isForeigner ? 10 : 7}
                        maxLength={13}
                        className="py-2"
                    />
                    <Button
                        disabled={isLoadingCtfPerson}
                        variant="outline-primary"
                        onClick={handleSearchCtf}
                        style={{ borderColor: '#b5430f', color: '#b5430f' }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#b5430f';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#b5430f';
                        }}
                    >
                        <FaSearch />
                    </Button>
                </InputGroup>

                <div className="d-flex justify-content-around">
                    <Form.Check
                        inline
                        disabled
                        label="Natural"
                        name="group1"
                        type="radio"
                        id="radio-natural"
                        value="N"
                        checked={type === 'N'}
                        onChange={handleChange}
                    />
                    <Form.Check
                        inline
                        disabled
                        label="Jurídica"
                        name="group1"
                        type="radio"
                        id="radio-juridica"
                        value="J"
                        checked={type === 'J'}
                        onChange={handleChange}
                    />
                    <Form.Check
                        inline
                        label="Extranjero/Pasaporte"
                        name="group1"
                        type="radio"
                        id="radio-extranjero"
                        value="NF"
                        checked={type === 'NF'}
                        onChange={handleChange}
                    />
                </div>
            </Form.Group>
            {isLoadingCtfPerson && <Loader />}
        </>
    );
};

export default DocIdGroup;
